import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { authService } from '../services/auth'
import { AccountIcon, BookIcon, ChevronDownIcon, HelpIcon, InfoIcon, LogoutIcon } from './icons/Icons'
import HelpModal from './HelpModal'
import './AccountMenu.css'

export const AccountMenu: React.FC = () => {
  const { t } = useTranslation('common')
  const [isOpen, setIsOpen] = useState(false)
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const user = authService.getUser()

  const toggleMenu = () => {
    setIsOpen(!isOpen)
  }

  const handleManageAccount = () => {
    setIsOpen(false)
    authService.openAccountPage()
  }

  const handleHelp = () => {
    setIsOpen(false)
    setIsHelpModalOpen(true)
  }

  const SUGGEST_IMPROVEMENT_EMAIL = 'contact-project+eda-pt-smarteda-main-70862491-issue-@incoming.gitlab.com'

  const handleSuggestImprovement = () => {
    setIsOpen(false)
    const subject = encodeURIComponent(t('suggestImprovementSubject'))
    const body = encodeURIComponent(t('suggestImprovementBody'))
    window.location.href = `mailto:${SUGGEST_IMPROVEMENT_EMAIL}?subject=${subject}&body=${body}`
  }

  const handleComplaintsBook = () => {
    setIsOpen(false)
    window.open('https://www.livroreclamacoes.pt/', '_blank', 'noopener,noreferrer')
  }

  const handleLogout = async () => {
    setIsOpen(false)
    await authService.logout()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false)
      buttonRef.current?.focus()
    }
  }

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  return (
    <>
      <div className="account-menu" ref={menuRef} onKeyDown={handleKeyDown}>
        <button
          ref={buttonRef}
          className="account-menu-button"
          onClick={toggleMenu}
          aria-label={t('accountMenu')}
          aria-expanded={isOpen}
          aria-haspopup="true"
          type="button"
        >
          <AccountIcon width={24} height={24} />
          <ChevronDownIcon width={20} height={20} className={`chevron ${isOpen ? 'open' : ''}`} />
        </button>

        {isOpen && (
          <div className="account-menu-dropdown" role="menu">
            <div className="account-menu-user">
              <span className="account-menu-email">{user.email}</span>
            </div>

            <div className="account-menu-divider" />

            <button
              className="account-menu-item"
              onClick={handleManageAccount}
              role="menuitem"
              type="button"
            >
              <AccountIcon width={20} height={20} />
              <span>{t('manageAccount')}</span>
            </button>

            <button
              className="account-menu-item"
              onClick={handleHelp}
              role="menuitem"
              type="button"
            >
              <HelpIcon width={20} height={20} />
              <span>{t('helpSupport')}</span>
            </button>

            <button
              className="account-menu-item"
              onClick={handleSuggestImprovement}
              role="menuitem"
              type="button"
            >
              <InfoIcon width={20} height={20} />
              <span>{t('suggestImprovement')}</span>
            </button>

            <button
              className="account-menu-item"
              onClick={handleComplaintsBook}
              role="menuitem"
              type="button"
            >
              <BookIcon width={20} height={20} />
              <span>{t('complaintsBook')}</span>
            </button>

            <div className="account-menu-divider" />

            <button
              className="account-menu-item logout"
              onClick={handleLogout}
              role="menuitem"
              type="button"
            >
              <LogoutIcon width={20} height={20} />
              <span>{t('logout')}</span>
            </button>
          </div>
        )}
      </div>

      <HelpModal isOpen={isHelpModalOpen} onClose={() => setIsHelpModalOpen(false)} />
    </>
  )
}

export default AccountMenu
